// Registration Flow - Types & Configuration

export type UserType = 'client';

export type RegisterStep =
  | 'personal_info'  // Step 1: Name, email
  | 'account_setup'  // Step 2: Username, password
  | 'success';       // Step 3: Confirmation

export interface RegisterFormData {
  user_type: UserType | null;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  username: string;
  password: string;
  password_confirm: string;
}

export interface RegisterState {
  step: RegisterStep;
  direction: 'forward' | 'backward';
  formData: RegisterFormData;
  errors: Record<string, string>;
  isSubmitting: boolean;
}

export const initialFormData: RegisterFormData = {
  user_type: 'client',
  first_name: '',
  last_name: '',
  email: '',
  phone_number: '',
  username: '',
  password: '',
  password_confirm: '',
};

export const stepOrder: RegisterStep[] = [
  'personal_info',
  'account_setup',
  'success',
];

export const stepConfig: Record<RegisterStep, {
  index: number;
  canGoBack: boolean;
  title: string;
  subtitle: string;
}> = {
  personal_info: {
    index: 0,
    canGoBack: false,
    title: 'Create your account',
    subtitle: 'Tell us about yourself to get started',
  },
  account_setup: {
    index: 1,
    canGoBack: true,
    title: 'Set up your login',
    subtitle: 'Choose your login credentials',
  },
  success: {
    index: 2,
    canGoBack: false,
    title: 'You\'re all set!',
    subtitle: '',
  },
};

// Get the next step in sequence
export function getNextStep(current: RegisterStep): RegisterStep | null {
  const currentIndex = stepOrder.indexOf(current);
  if (currentIndex < 0 || currentIndex >= stepOrder.length - 1) return null;
  return stepOrder[currentIndex + 1];
}

// Get the previous step in sequence
export function getPreviousStep(current: RegisterStep): RegisterStep | null {
  const currentIndex = stepOrder.indexOf(current);
  if (currentIndex <= 0) return null;
  return stepOrder[currentIndex - 1];
}

// Validation helpers
export function validatePersonalInfo(data: RegisterFormData): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!data.first_name.trim()) {
    errors.first_name = 'First name is required';
  }

  if (!data.last_name.trim()) {
    errors.last_name = 'Last name is required';
  }

  if (!data.email.trim()) {
    errors.email = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.email = 'Please enter a valid email address';
  }

  return errors;
}

export function validateAccountSetup(data: RegisterFormData): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!data.username.trim()) {
    errors.username = 'Username is required';
  } else if (data.username.length < 3) {
    errors.username = 'Username must be at least 3 characters';
  }

  if (!data.password) {
    errors.password = 'Password is required';
  } else if (data.password.length < 8) {
    errors.password = 'Password must be at least 8 characters';
  }

  if (!data.password_confirm) {
    errors.password_confirm = 'Please confirm your password';
  } else if (data.password !== data.password_confirm) {
    errors.password_confirm = 'Passwords do not match';
  }

  return errors;
}

